<template>
  <div class="status-tiles">
    <button
      v-for="status in STATUS_ORDER"
      :key="status"
      type="button"
      class="status-tile"
      :class="[`status-tile--${colorFor(status)}`, { 'status-tile--active': isActive(status) }]"
      @click="onTileClick(status)">
      <div class="status-tile__count">{{ countFor(status) }}</div>
      <div class="status-tile__label">
        <q-icon
          :name="STATUS_ICON[status]"
          size="14px" />
        {{ labelFor(status) }}
      </div>
    </button>
  </div>
</template>

<script setup lang="ts">
import { useCompositeI18n } from 'src/CrudManager/Core/composables/useCompositeI18n'
import { inject } from 'vue'
import { queueManagerKey } from '../composables/queueManagerKey'
import type { AiQueueStatus } from '../types'
import { STATUS_COLOR, STATUS_ICON, STATUS_ORDER } from '../utils/statusMaps'

/**
 * Clickable status tiles: global counts that double as the table's status
 * filter. The tile order reads like the pipeline itself — waiting, working,
 * needs you — then the three outcomes.
 */
const queueManager = inject(queueManagerKey)!

const { t } = useCompositeI18n('core.AiDocumentProcessor')

const colorFor = (status: AiQueueStatus | 'all'): string => (status === 'all' ? 'primary' : STATUS_COLOR[status])

const labelFor = (status: AiQueueStatus | 'all'): string => (status === 'all' ? t('tiles.all') : t(`status.${status}`))

const countFor = (status: AiQueueStatus | 'all'): number => queueManager.statusCounts.value?.[status] ?? 0

const isActive = (status: AiQueueStatus | 'all'): boolean => queueManager.statusFilter.value === status

const onTileClick = (status: AiQueueStatus | 'all'): void => {
  void queueManager.setStatusFilter(isActive(status) && status !== 'all' ? 'all' : status)
}
</script>

<style lang="scss" scoped>
.status-tiles {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 8px;

  // Phones: one swipeable row instead of a tall grid
  @media (max-width: 599px) {
    display: flex;
    overflow-x: auto;
    scroll-snap-type: x proximity;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }

    .status-tile {
      flex: 0 0 auto;
      min-width: 104px;
      scroll-snap-align: start;
    }
  }
}

.status-tile {
  --tile-color: var(--q-primary);
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 10px 12px 8px;
  border: 1px solid color-mix(in srgb, var(--tile-color) 25%, transparent);
  border-top: 3px solid color-mix(in srgb, var(--tile-color) 75%, transparent);
  border-radius: 8px;
  background: color-mix(in srgb, var(--tile-color) 4%, transparent);
  cursor: pointer;
  text-align: left;
  font: inherit;
  color: inherit;
  transition:
    background-color 0.15s ease,
    border-color 0.15s ease,
    transform 0.1s ease;

  &:hover {
    background: color-mix(in srgb, var(--tile-color) 12%, transparent);
  }

  &:focus-visible {
    outline: 2px solid var(--tile-color);
    outline-offset: 2px;
  }

  &--active {
    background: color-mix(in srgb, var(--tile-color) 18%, transparent);
    border-color: color-mix(in srgb, var(--tile-color) 60%, transparent);
  }

  &__count {
    font-size: 1.45rem;
    font-weight: 700;
    line-height: 1.1;
    font-variant-numeric: tabular-nums;
  }

  &__label {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 0.68rem;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    opacity: 0.75;
    white-space: nowrap;
  }
}

.status-tile--primary { --tile-color: var(--q-primary); }
.status-tile--warning { --tile-color: var(--q-warning); }
.status-tile--info { --tile-color: var(--q-info); }
.status-tile--accent { --tile-color: var(--q-accent); }
.status-tile--positive { --tile-color: var(--q-positive); }
.status-tile--negative { --tile-color: var(--q-negative); }
.status-tile--muted { --tile-color: #9e9e9e; }
</style>
